import { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector, legsActions, runnersActions } from '../store/index'
import { AppShell } from '../components/layout/AppShell'
import { legService } from '../services/legService'
import { runnerService } from '../services/runnerService'
import { formatDuration } from '../utils/formatTime'
import { formatPace } from '../utils/paceCalc'

export function LogPage() {
  const dispatch = useAppDispatch()
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0 }, [])
  const logs = useAppSelector(state => state.legs.logs)
  const [selectedLog, setSelectedLog] = useState<any | null>(null)
  const [editStartTime, setEditStartTime] = useState('')
  const [editStartSeconds, setEditStartSeconds] = useState('00')
  const [editEndTime, setEditEndTime] = useState('')
  const [editEndSeconds, setEditEndSeconds] = useState('00')
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const [logs, fetchedRunners, fetchedLegs] = await Promise.all([
          legService.getLegLogs(),
          runnerService.getRunners(),
          legService.getLegs(),
        ])
        dispatch(legsActions.setLegLogs(logs))
        dispatch(runnersActions.setRunners(fetchedRunners))
        dispatch(legsActions.setLegAssignments(fetchedLegs))
      } catch (error) {
        console.error('Failed to load logs:', error)
      }
    }

    loadLogs()
  }, [dispatch])

  // Convert a UTC ISO string to YYYY-MM-DDTHH:mm (seconds handled separately)
  const toLocalInputString = (isoString: string): string => {
    const d = new Date(isoString)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const toLocalSeconds = (isoString: string): string => {
    const d = new Date(isoString)
    return String(d.getSeconds()).padStart(2, '0')
  }

  // Build ISO string from datetime-local value + seconds field
  const buildISOString = (datetimeLocal: string, seconds: string): string | null => {
    if (!datetimeLocal) return null
    const sec = Math.min(59, Math.max(0, parseInt(seconds) || 0))
    return new Date(`${datetimeLocal}:${String(sec).padStart(2, '0')}`).toISOString()
  }

  const openLogDetails = (log: any) => {
    setSelectedLog(log)
    setEditStartTime(log.startTime ? toLocalInputString(log.startTime) : '')
    setEditStartSeconds(log.startTime ? toLocalSeconds(log.startTime) : '00')
    setEditEndTime(log.endTime ? toLocalInputString(log.endTime) : '')
    setEditEndSeconds(log.endTime ? toLocalSeconds(log.endTime) : '00')
    setIsEditing(false)
  }

  const handleSaveLog = async () => {
    if (!selectedLog) return
    try {
      const startTime = buildISOString(editStartTime, editStartSeconds)
      const endTime = buildISOString(editEndTime, editEndSeconds)

      await legService.updateLegLog(selectedLog.id, {
        startTime,
        endTime,
        notes: selectedLog.notes,
      })

      // Reload all logs so cascaded adjacent changes are reflected
      const refreshed = await legService.getLegLogs()
      dispatch(legsActions.setLegLogs(refreshed))
      setIsEditing(false)
      setSelectedLog(null)
    } catch (error) {
      console.error('Failed to save log:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save log'}`)
    }
  }

  const handleExport = () => {
    // Generate CSV
    const headers = ['Leg #', 'Runner', 'Van', 'Distance', 'Duration', 'Pace', 'Start Time', 'End Time']
    const rows = logs.map(log => [
      log.legNumber || '-',
      log.runnerName || '-',
      log.vanNumber || '-',
      log.distanceMiles ? log.distanceMiles.toFixed(1) : '-',
      log.actualDurationSeconds ? formatDuration(log.actualDurationSeconds) : '-',
      log.actualPacePerMile ? `${log.actualPacePerMile.toFixed(1)} min/mi` : '-',
      log.startTime || '-',
      log.endTime || '-',
    ])

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `relay_race_${new Date().toISOString()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <AppShell>
      <div ref={scrollRef} className="h-full overflow-y-auto p-3 space-y-3 pb-24">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          RTO Log
        </h1>

        {/* Summary Stats */}
        <div className="flex gap-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <span>Total Legs: <span className="text-blue-700">{logs.length}</span></span>
          <span>Completed: <span className="text-green-700">{logs.filter(l => l.endTime).length}</span></span>
        </div>

        {/* Logs Table */}
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-700 dark:text-gray-300">Leg Details</h3>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No race data yet</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-1.5 py-2 text-center font-semibold">Van</th>
                    <th className="px-1.5 py-2 text-left font-semibold">Runner</th>
                    <th className="px-1.5 py-2 text-center font-semibold">Leg</th>
                    <th className="px-1.5 py-2 text-center font-semibold">Dist</th>
                    <th className="px-1.5 py-2 text-center font-semibold">Time</th>
                    <th className="px-1.5 py-2 text-center font-semibold">Pace</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr
                      key={idx}
                      onClick={() => openLogDetails(log)}
                      className={`border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        log.endTime ? 'bg-green-50 dark:bg-green-950' : log.startTime ? 'bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                    >
                      <td className="px-1.5 py-1.5 text-center">{log.vanNumber || '-'}</td>
                      <td className="px-1.5 py-1.5">{log.runnerName || 'Unassigned'}</td>
                      <td className="px-1.5 py-1.5 text-center font-semibold">
                        <span className="inline-flex items-center gap-0.5">
                          {log.legNumber || '-'}
                          {log.manuallyAdjusted && (
                            <span title="Manually adjusted" className="text-amber-500 text-xs leading-none">✎</span>
                          )}
                        </span>
                      </td>
                      <td className="px-1.5 py-1.5 text-center">{log.distanceMiles?.toFixed(1) || '-'}</td>
                      <td className="px-1.5 py-1.5 text-center font-mono">
                        {log.actualDurationSeconds ? formatDuration(log.actualDurationSeconds) : '-'}
                      </td>
                      <td className="px-1.5 py-1.5 text-center font-mono">
                        {log.actualPacePerMile ? formatPace(log.actualPacePerMile) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button
            onClick={handleExport}
            className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
        </div>

        {/* Edit Log Modal */}
        {selectedLog && (() => {
          const startIso = buildISOString(editStartTime, editStartSeconds)
          const endIso = buildISOString(editEndTime, editEndSeconds)
          const startMs = startIso ? new Date(startIso).getTime() : null
          const endMs = endIso ? new Date(endIso).getTime() : null
          const durationSeconds = startMs && endMs ? Math.floor((endMs - startMs) / 1000) : null
          const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', {
            timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
          })
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg font-bold mb-1">Leg {selectedLog.legNumber}: {selectedLog.runnerName}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{selectedLog.distanceMiles?.toFixed(2)} mi • Van {selectedLog.vanNumber}</p>

                {/* Times display */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Start</span>
                    <span className="font-mono font-semibold">{startIso ? fmtTime(startIso) : '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">End</span>
                    <span className="font-mono font-semibold">{endIso ? fmtTime(endIso) : '—'}</span>
                  </div>
                  {durationSeconds !== null && (
                    <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-600 pt-2">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Duration</span>
                      <span className="font-mono font-bold text-blue-700 text-base">{formatDuration(durationSeconds)}</span>
                    </div>
                  )}
                </div>

                {/* Editable fields — only shown in edit mode */}
                {isEditing && (
                  <div className="space-y-3 mb-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Edit Start Time</label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="datetime-local"
                          value={editStartTime}
                          onChange={(e) => setEditStartTime(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                        <div className="flex flex-col items-center">
                          <label className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Sec</label>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            value={editStartSeconds}
                            onChange={(e) => setEditStartSeconds(e.target.value.padStart(2, '0'))}
                            className="w-14 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Edit End Time</label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="datetime-local"
                          value={editEndTime}
                          onChange={(e) => setEditEndTime(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        />
                        <div className="flex flex-col items-center">
                          <label className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Sec</label>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            value={editEndSeconds}
                            onChange={(e) => setEditEndSeconds(e.target.value.padStart(2, '0'))}
                            className="w-14 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-5">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveLog}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setSelectedLog(null)}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    </AppShell>
  )
}
